// Shared premium animated listbox (presentation-only replacement for native/basic selects).
// options: [{ value, label, Icon, tone, disabled, badge }]
// TAX-CATEGORY-PREMIUM-SELECT-002 — optional `searchable` filter field + per-option `badge` (e.g. "New").
import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

const CLOSE_MS = 160;

const PremiumListbox = ({ value, options, onChange, disabled = false, placeholder = 'Select', ariaLabel, testId, className = '', PlaceholderIcon = null, placeholderTone = 'tcp-tile-indigo', searchable = false, searchPlaceholder = 'Search…' }) => {
  const current = value || '';
  const selectedIdx = options.findIndex((o) => o.value === current);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  // visible = options after the search filter; `active` indexes into `visible`
  const visible = q ? options.filter((o) => (o.label || '').toLowerCase().includes(q)) : options;
  const visibleSelectedIdx = visible.findIndex((o) => o.value === current);
  const [active, setActive] = useState(Math.max(0, selectedIdx));
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const closeTimer = useRef(null);
  const listId = useId();

  const close = (refocus = true) => {
    if (!open) return;
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => { setOpen(false); setClosing(false); setQuery(''); }, CLOSE_MS);
    if (refocus && btnRef.current) btnRef.current.focus();
  };
  const openMenu = () => {
    if (disabled) return;
    clearTimeout(closeTimer.current); setClosing(false); setQuery('');
    setActive(selectedIdx >= 0 ? selectedIdx : options.findIndex((o) => !o.disabled));
    setOpen(true);
  };
  const pick = (idx) => {
    const o = visible[idx];
    if (!o || o.disabled) return;
    if (o.value !== current) onChange(o.value);
    close();
  };
  const step = (dir) => setActive((i) => {
    let n = i;
    for (let k = 0; k < visible.length; k += 1) {
      n = Math.min(visible.length - 1, Math.max(0, n + dir));
      if (!visible[n]?.disabled) return n;
      if (n === 0 || n === visible.length - 1) break;
    }
    return i;
  });

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (open && !closing && searchable && searchRef.current) searchRef.current.focus();
  }, [open, closing, searchable]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) close(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('touchstart', onDoc); };
  });

  useEffect(() => {
    if (!open || closing || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, open, closing]);

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openMenu(); }
      return;
    }
    const typing = searchable && e.target === searchRef.current;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); step(1); break;
      case 'ArrowUp': e.preventDefault(); step(-1); break;
      case 'Home': if (typing) break; e.preventDefault(); setActive(0); break;
      case 'End': if (typing) break; e.preventDefault(); setActive(visible.length - 1); break;
      case 'Enter': e.preventDefault(); pick(active); break;
      case ' ': if (typing) break; e.preventDefault(); pick(active); break;
      case 'Escape': e.preventDefault(); close(); break;
      case 'Tab': close(false); break;
      default: break;
    }
  };

  const TriggerIcon = selected ? selected.Icon : PlaceholderIcon;
  const triggerTone = selected ? selected.tone : placeholderTone;
  const isOpen = open && !closing;

  return (
    <div ref={rootRef} className={`tcs-root ${className}`} onKeyDown={onKeyDown}>
      <button
        ref={btnRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        className={`tcs-trigger ${isOpen ? 'is-open' : ''} ${selected && selected.value ? 'is-set' : ''}`}
        data-testid={testId}
        data-value={current}
      >
        {TriggerIcon && <span className={`tcp-tile ${triggerTone} tcs-trigger-icon`}><TriggerIcon className="w-3.5 h-3.5" /></span>}
        <span className={`tcs-trigger-label ${selected ? '' : 'is-placeholder'}`} data-testid={`${testId}-value`}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`tcs-chevron ${isOpen ? 'is-open' : ''}`} />
      </button>
      {open && (
        <div className={`tcs-menu ${closing ? 'is-closing' : ''}`} data-testid={`${testId}-menu`}>
          {searchable && (
            <div className="tcs-search">
              <Search className="tcs-search-icon" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                placeholder={searchPlaceholder}
                aria-label={`${ariaLabel || 'Options'} search`}
                aria-controls={listId}
                aria-activedescendant={visible.length ? `${listId}-opt-${active}` : undefined}
                autoComplete="off"
                data-testid={`${testId}-search`}
              />
            </div>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-activedescendant={`${listId}-opt-${active}`}
            className="tcs-list"
          >
            {visible.map((o, idx) => {
              const isSel = idx === visibleSelectedIdx;
              return (
                <li
                  key={o.value || '__unset'}
                  id={`${listId}-opt-${idx}`}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={o.disabled || undefined}
                  data-idx={idx}
                  className={`tcs-option ${isSel ? 'is-selected' : ''} ${idx === active ? 'is-active' : ''} ${o.disabled ? 'is-disabled' : ''}`}
                  onMouseEnter={() => { if (!o.disabled) setActive(idx); }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(idx)}
                  data-testid={`${testId}-option-${o.value || 'unset'}`}
                >
                  <span className={`tcp-tile ${o.tone || 'tcp-tile-indigo'} tcs-option-icon`}>{o.Icon ? <o.Icon className="w-3.5 h-3.5" /> : null}</span>
                  <span className="tcs-option-label">{o.label}</span>
                  {o.badge && <span className="tcs-option-badge" data-testid={`${testId}-badge-${o.value}`}>{o.badge}</span>}
                  {isSel && <span className="tcs-option-check"><Check className="w-3.5 h-3.5" /></span>}
                </li>
              );
            })}
            {visible.length === 0 && <li className="tcs-empty" role="presentation" data-testid={`${testId}-empty`}>No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PremiumListbox;
